/**
 * A countdown timer custom element.
 *
 * Reads an ISO end datetime from `data-end` (interpreted in the shopper's
 * local timezone) and updates its segment elements once per second.
 * When `data-hide-when-complete` is 'true', the element hides itself on expiry;
 * otherwise it clamps at zero.
 */
class CountdownTimer extends HTMLElement {
  /** @type {number | undefined} */
  #interval;

  /** @type {number} */
  #endTime = NaN;

  /** @type {Record<string, HTMLElement | null>} */
  #segments = {};

  connectedCallback() {
    this.#endTime = Date.parse(this.dataset.end || '');

    if (Number.isNaN(this.#endTime)) {
      this.hidden = true;
      return;
    }

    this.#segments = {
      days: this.querySelector('[data-segment="days"]'),
      hours: this.querySelector('[data-segment="hours"]'),
      minutes: this.querySelector('[data-segment="minutes"]'),
      seconds: this.querySelector('[data-segment="seconds"]'),
    };

    this.#interval = window.setInterval(() => this.#update(), 1000);
    this.#update();
  }

  disconnectedCallback() {
    window.clearInterval(this.#interval);
  }

  #update() {
    let remaining = this.#endTime - Date.now();

    if (remaining <= 0) {
      remaining = 0;
      window.clearInterval(this.#interval);

      if (this.dataset.hideWhenComplete === 'true') {
        this.hidden = true;
        return;
      }
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const values = {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
    };

    for (const [key, element] of Object.entries(this.#segments)) {
      if (!element) continue;
      const value = values[/** @type {keyof typeof values} */ (key)];
      const text = key === 'days' ? String(value) : String(value).padStart(2, '0');
      if (element.textContent !== text) element.textContent = text;
    }
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
